import { useTranslation } from '@hbs-us/ui';
import { getRouteApi } from '@tanstack/react-router';
import { RouteSkeleton } from '../_ops/RouteSkeleton.js';
import { InvoicesView } from './InvoicesView.js';
import { useInvoicesQuery } from './useInvoicesQuery.js';

/**
 * The `/invoices` route component (promoted from a ComingSoon stub this plan).
 * Reads the typed search params (`show_archived`), fires the coarse-endpoint
 * TanStack Query, and renders the faithful 1:1 `InvoicesView` on success.
 * Loading → a page-shaped `RouteSkeleton`; error → announced error state with a retry
 * that refetches (the reports `ReportsRoute` 3-state contract, verbatim tokens).
 *
 * The server computes the enriched list + rollups; this component never
 * re-aggregates and never sends a tenant id (SEC-11).
 */

const routeApi = getRouteApi('/invoices');

export function InvoicesRoute() {
  const { t } = useTranslation();
  const search = routeApi.useSearch();
  const query = useInvoicesQuery(search);

  if (query.isPending) {
    return <RouteSkeleton label={t('invoices:title')} rows={8} columns={4} />;
  }

  if (query.isError) {
    return (
      <section
        role="alert"
        aria-live="assertive"
        className="flex h-full flex-col items-center justify-center gap-4 bg-background p-16 text-center"
      >
        <h1 className="font-display font-semibold text-2xl text-foreground">
          {t('invoices:error.heading')}
        </h1>
        <p className="max-w-sm text-muted-foreground text-sm">{t('invoices:error.body')}</p>
        <button
          type="button"
          onClick={() => void query.refetch()}
          className="inline-flex h-11 items-center justify-center rounded-md bg-secondary px-4 font-medium text-secondary-foreground text-sm ring-offset-background transition-colors hover:bg-secondary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        >
          {t('invoices:error.retry')}
        </button>
      </section>
    );
  }

  return <InvoicesView data={query.data} search={search} />;
}
